import React from 'react';
import { User, Home } from 'lucide-react';

/**
 * OwnershipBadge - Consistent Personal/Household indicator across the app
 * 
 * @param {boolean} isHousehold - Whether the item belongs to a household (true) or is personal (false)
 * @param {string} variant - Display variant: 'badge' (default), 'icon-only', 'text', 'compact'
 * @param {string} size - Size: 'sm', 'md' (default), 'lg'
 * @param {boolean} showLabel - Whether to show the text label (default: true for badge/text, false for icon-only)
 */
function OwnershipBadge({ 
  isHousehold, 
  variant = 'badge', 
  size = 'md',
  showLabel
}) {
  // Size configurations
  const sizes = {
    sm: { icon: 14, fontSize: '0.7rem', padding: '2px 6px', gap: 4, badgeSize: 22 },
    md: { icon: 16, fontSize: '0.75rem', padding: '3px 8px', gap: 5, badgeSize: 26 },
    lg: { icon: 18, fontSize: '0.85rem', padding: '4px 10px', gap: 6, badgeSize: 30 }
  };

  const s = sizes[size] || sizes.md;
  
  // Determine if label should show based on variant
  const displayLabel = showLabel !== undefined 
    ? showLabel 
    : variant !== 'icon-only';

  const Icon = isHousehold ? Home : User;
  const label = isHousehold ? 'Household' : 'Personal';
  
  // Color scheme - Green for Household, Blue for Personal
  const colors = isHousehold 
    ? { bg: 'rgba(34, 197, 94, 0.15)', border: 'rgba(34, 197, 94, 0.3)', text: '#22c55e', iconColor: '#22c55e' }
    : { bg: 'rgba(59, 130, 246, 0.15)', border: 'rgba(59, 130, 246, 0.3)', text: '#3b82f6', iconColor: '#3b82f6' };

  // Icon-only variant
  if (variant === 'icon-only') {
    return (
      <span
        title={label}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: s.badgeSize,
          height: s.badgeSize,
          borderRadius: '50%',
          backgroundColor: colors.bg,
          border: `1px solid ${colors.border}`,
          color: colors.iconColor,
          flexShrink: 0
        }}
      >
        <Icon size={s.icon} strokeWidth={2.5} />
      </span>
    );
  }

  // Compact variant - smaller, inline
  if (variant === 'compact') {
    return (
      <span
        title={label}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: `${s.gap - 1}px`,
          color: colors.text,
          fontSize: s.fontSize,
          fontWeight: 500
        }}
      >
        <Icon size={s.icon - 2} />
        {displayLabel && <span>{label}</span>}
      </span>
    );
  }

  // Text variant - just icon and text, no background
  if (variant === 'text') {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: `${s.gap}px`,
          color: colors.text,
          fontSize: s.fontSize,
          fontWeight: 500
        }}
      >
        <Icon size={s.icon} />
        {displayLabel && <span>{label}</span>}
      </span>
    );
  }

  // Default badge variant
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: `${s.gap}px`,
        padding: s.padding,
        borderRadius: '4px',
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
        color: colors.text,
        fontSize: s.fontSize,
        fontWeight: 500,
        whiteSpace: 'nowrap'
      }}
    >
      <Icon size={s.icon} />
      {displayLabel && <span>{label}</span>}
    </span>
  );
}

export default OwnershipBadge;
